import { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import Multiselect, { MultiValue } from 'react-select';
import { Button } from '../Controls/Button';

/** Minimal shape for building operator options. */
export interface ReportOperatorOption {
    operatorId: number;
    name?: string | null;
}

export type OperatorSelectOption = { label: string; value: number };

export function toReportOperatorOptions(
    operators: ReportOperatorOption[] | undefined
): OperatorSelectOption[] {
    return (operators ?? [])
        .filter((o) => o.operatorId != null)
        .map((o) => ({
            label: o.name || `Operator ${o.operatorId}`,
            value: o.operatorId,
        }));
}

/**
 * Multi-operator selection for admin reports. When `syncSelectedIds` is undefined, no sync effect runs.
 * When it is defined (including `[]`), selection is reset whenever `syncSelectedIds` or options change.
 */
export function useReportOperatorMultiSelect(
    operators: ReportOperatorOption[] | undefined,
    syncSelectedIds?: number[] | null
) {
    const operatorOptions = useMemo(
        () => toReportOperatorOptions(operators),
        [operators]
    );

    const [selectedOptions, setSelectedOptions] = useState<OperatorSelectOption[]>([]);

    useEffect(() => {
        if (syncSelectedIds === undefined) return;
        const ids = syncSelectedIds ?? [];
        setSelectedOptions(operatorOptions.filter((o) => ids.includes(o.value)));
    }, [syncSelectedIds, operatorOptions]);

    const selectedIds = useMemo(
        () => selectedOptions.map((o) => o.value),
        [selectedOptions]
    );

    const setSelectedIds = useCallback(
        (ids: number[]) => {
            setSelectedOptions(operatorOptions.filter((o) => ids.includes(o.value)));
        },
        [operatorOptions]
    );

    const onMultiChange = useCallback((opts: MultiValue<OperatorSelectOption>) => {
        setSelectedOptions(opts ? [...opts] : []);
    }, []);

    const selectAll = useCallback(
        () => setSelectedOptions(operatorOptions),
        [operatorOptions]
    );

    const clearAll = useCallback(() => setSelectedOptions([]), []);

    return {
        operatorOptions,
        selectedOptions,
        selectedIds,
        onMultiChange,
        selectAll,
        clearAll,
        setSelectedIds,
    };
}

export function ReportOperatorMultiSelectRow({
    operatorOptions,
    selectedOptions,
    onMultiChange,
    selectAll,
    clearAll,
    label = 'Operators',
    className = 'mb-3',
}: {
    operatorOptions: OperatorSelectOption[];
    selectedOptions: OperatorSelectOption[];
    onMultiChange: (opts: MultiValue<OperatorSelectOption>) => void;
    selectAll: () => void;
    clearAll: () => void;
    label?: string;
    className?: string;
}) {
    return (
        <Row className={className}>
            <Col>
                <Form.Group>
                    <div className="d-flex align-items-center mb-2 flex-nowrap">
                        <Form.Label className="mb-0 me-3 flex-shrink-0">{label}</Form.Label>
                        {operatorOptions.length > 1 && (
                            <div className="flex-shrink-0 me-2">
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline-primary"
                                    className="me-1"
                                    onClick={selectAll}
                                >
                                    Select All
                                </Button>
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline-secondary"
                                    onClick={clearAll}
                                >
                                    None
                                </Button>
                            </div>
                        )}
                        <div className="flex-grow-1 min-w-0">
                            <Multiselect
                                onChange={onMultiChange}
                                value={selectedOptions}
                                isMulti
                                options={operatorOptions}
                                placeholder="Select operators..."
                                isClearable={false}
                            />
                        </div>
                    </div>
                </Form.Group>
            </Col>
        </Row>
    );
}
